import { AxiosError } from 'axios';
import { getDateRange1Year } from 'src/utils/formatDate';
import serviceToken from './requestToken';
import { BaseResponse } from './types';

const RETAILER_BASE_API = process.env.REACT_APP_RETAILER_BASE_API;
const API_URL_DSZ = process.env.REACT_APP_API_URL_DSZ;

export type RetailerInfo = {
  // base info
  id: number;
  email: string;
  created_at: string;
  updated_at: string;
  group_id: number;
  lastname: string;
  firstname: string;

  // business details
  rs_company: string;
  rs_abn: string;
  rs_telephone: string;
  rs_credit_limit: string;
  rs_account_status: number;
  ds_email_secondary: string;

  // account manager
  rs_account_manager: string;
  rs_account_manager_email: string;

  // api details
  rs_allow_api_pwd: string;
};

export const getRetailerInfoSvc = async (
  retailerId: string
): Promise<BaseResponse<RetailerInfo>> => {
  const res = await serviceToken.get(
    RETAILER_BASE_API + `retailers/${retailerId}`,
    {
      params: {}
    }
  );

  return res.data;
};

export type RetailerInfoUpdate = Partial<
  Pick<RetailerInfo, 'ds_email_secondary'>
>;

export const updateRetailerInfoSvc = async (
  retailerId: string,
  data: RetailerInfoUpdate
): Promise<BaseResponse<RetailerInfo>> => {
  const res = await serviceToken.put(
    RETAILER_BASE_API + `retailers/${retailerId}`,
    data
  );
  return res.data;
};

export type RetailerBillingInfo = {
  customer_id: number;
  email: string;
  telephone: string;
  country: CountryCode;
  postcode: string;
  state: string;
  suburb: string;
  street: string;
  company: string;
  lastname: string;
  firstname: string;
  address_type: 'billing' | 'shipping';
};

export type RetailerBillingAddressUpdate = Partial<
  Pick<
    RetailerBillingInfo,
    | 'country'
    | 'postcode'
    | 'state'
    | 'suburb'
    | 'street'
    | 'telephone'
    | 'lastname'
    | 'firstname'
  >
>;

export const getRetailerBillingAddressSvc = async (
  retailerId: string
): Promise<BaseResponse<RetailerBillingInfo>> => {
  const res = await serviceToken.get(
    RETAILER_BASE_API + `retailers/${retailerId}/address/billing`,
    {
      params: {}
    }
  );

  return res.data;
};

export const updateRetailerBillingAddressSvc = async (
  retailerId: string,
  data: RetailerBillingAddressUpdate
): Promise<BaseResponse<RetailerBillingInfo>> => {
  const res = await serviceToken.put(
    RETAILER_BASE_API + `retailers/${retailerId}/address/billing`,
    data
  );
  return res.data;
};

export type CountryCode = 'AU' | 'NZ';

export type CountryRegionInfo = {
  region_id: number;
  country_id: CountryCode;
  default_name: string;
  code: string;
};

export const getCountryRegionSvc = async (
  country: CountryCode
): Promise<BaseResponse<CountryRegionInfo[]>> => {
  const res = await serviceToken.get(
    RETAILER_BASE_API + `retailers/country-region`,
    {
      params: { country_id: country }
    }
  );
  return res.data;
};

export type PostcodeInfo = {
  suburb: string;
  postcode: string;
  state: string;
  zone_rate_code: string;
};

export const getPostcodeSvc = async (
  country: CountryCode,
  params: {
    postcode?: string;
    suburb?: string;
  }
): Promise<PostcodeInfo[]> => {
  const res = await serviceToken.get(API_URL_DSZ + `/postcodes/${country}`, {
    params
  });
  return res.data.result;
};

export type VerificationCodeResponse = {
  ttl: number;
  exists: boolean;
};

export type VerificationCodeVerify = {
  code: string;
};

export type VerificationCodeVerifyResponse = {};

export enum VerificationCodeType {
  PASSWORD = 'RP',
  API_PASSWORD = 'RAP'
}

export const sendVerificationCodeSvc = async (
  retailerId: string,
  type: VerificationCodeType
): Promise<BaseResponse<VerificationCodeResponse>> => {
  const res = await serviceToken.post(
    RETAILER_BASE_API + `verification/send-code`,
    { customerId: retailerId, type: type }
  );
  return res.data;
};

export const verifyCodeSvc = async (
  retailerId: string,
  data: VerificationCodeVerify
): Promise<BaseResponse<VerificationCodeVerifyResponse>> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      // we only check the code is not empty, we will check the code in the password change api
      if (data.code && data.code.trim() !== '') {
        resolve({
          success: true,
          message: 'Please change your password',
          data: {}
        });
      } else {
        reject(new Error('Invalid verification code. Please try again.'));
      }
    }, 800);
  });
};

// Password change types and service
export type PasswordChangeData = {
  oldPassword: string;
  newPassword: string;
  verificationCode: string;
};

export type PasswordChangeResponse = {};

export const changePasswordSvc = async (
  retailerId: string,
  data: PasswordChangeData
): Promise<BaseResponse<PasswordChangeResponse>> => {
  const res = await serviceToken.post(
    RETAILER_BASE_API + `retailers/${retailerId}/reset-password`,
    data
  );
  return res.data;
};

export const getOrderIdByIncrementId = async (incrementId: string) => {
  const data = await serviceToken.get(
    RETAILER_BASE_API + `/orders/${incrementId}/orderId`
  );
  return data.data;
};

export const getCustomerServiceTicketsUnreadCountSvc = async (
  from?: string,
  to?: string
) => {
  if (!from || !to) {
    from = getDateRange1Year().from.format('YYYY-MM-DD');
    to = getDateRange1Year().to.format('YYYY-MM-DD');
  }
  if (from > to)
    return { error: 'Invalid date range: from is after to', count: 0 };
  const params = { from, to };

  try {
    const { data } = await serviceToken.get(
      RETAILER_BASE_API + 'customer-service/tickets/unread-count',
      { params }
    );

    if (!data.success) {
      console.error(`Error fetching unread count: ${data.message}`);
      return {
        error: `Error fetching unread count: ${data.message}`,
        count: 0
      };
    }

    return {
      count: data.data?.count ?? 0,
      error: null
    };
  } catch (error) {
    console.error('Error fetching unread count:', error);
    if (error instanceof AxiosError && error.code === 'ERR_CANCELED') {
      return {
        error: null,
        count: 0
      };
    }
    return {
      error: `Error fetching unread count: ${error}`,
      count: 0
    };
  }
};

export const getCustomerServiceTicketsSvc = async (
  page: number,
  status: 'open' | 'closed' | '',
  from?: string,
  to?: string,
  dszOrderId?: string,
  subjectType?: number[],
  limit = 20,
): Promise<{
  tickets: any[];
  currentPage: number;
  totalPages: number;
  total: number;
  error?: string;
}> => {
  if (!from || !to) {
    from = getDateRange1Year().from.format('YYYY-MM-DD');
    to = getDateRange1Year().to.format('YYYY-MM-DD');
  }
  if (from > to || page < 1) {
    return {
      tickets: [],
      currentPage: 1,
      totalPages: 1,
      total: 0,
      error: 'Invalid date range: from is after to or page is less than 1'
    };
  }
  const params: {
    page: number;
    limit: number;
    from: string;
    to: string;
    status?: 'open' | 'closed' | '';
    order_id?: string;
    subject_type?: number[],
  } = {
    page,
    limit,
    from,
    to
  };
  if (status) {
    params.status = status;
  }
  if (dszOrderId) {
    params.order_id = dszOrderId;
  }
  if (subjectType) {
    params.subject_type = subjectType;
  }
  try {
    const { data } = await serviceToken.get(
      RETAILER_BASE_API + 'customer-service/tickets',
      { params }
    );

    if (!data.success) {
      console.error(`Error fetching customer service tickets: ${data.message}`);
      return {
        tickets: [],
        currentPage: 1,
        totalPages: 1,
        total: 0,
        error: `Error fetching customer service tickets: ${data.message}`
      };
    }

    const res = {
      tickets:
        data.data?.tickets?.map((ticket: any) => {
          return {
            dszOrderId: ticket.order_id,
            ticketId: ticket.id,
            yourOrderId: ticket.retailer_order_id || '',
            customer: ticket.customer?.name || '',
            subject: ticket.subject,
            subjectType: ticket.subject_type || 0, // default is 0: Other
            updatedAt: ticket.updated_at,
            dateCreated: ticket.created_at,
            externalTicketLastReadAt: ticket.external_ticket_last_read_at,
            externalTicketUpdatedAt: ticket.external_ticket_updated_at,
            status: ticket.status
          };
        }) ?? [],
      currentPage: data.data?.current_page ?? 1,
      totalPages: data.data?.total_pages ?? 1,
      total: data.data?.total ?? 0
    };

    return res;
  } catch (error) {
    console.error(`Error fetching customer service tickets: ${error}`);
    if (error instanceof AxiosError && error.code === 'ERR_CANCELED') {
      return {
        tickets: [],
        currentPage: 1,
        totalPages: 1,
        total: 0
      };
    }
    return {
      tickets: [],
      currentPage: 1,
      totalPages: 1,
      total: 0,
      error: `Error fetching customer service tickets: ${error}`
    };
  }
};

export const markTicketAsReadSvc = async (ticketId: string) => {
  const { data } = await serviceToken.put(
    RETAILER_BASE_API + `customer-service/tickets/${ticketId}`,
    {}
  );
  return data;
};
